<template lang="pug">
.brackets(
  :class="takeModifier"
)
  template(
    v-for="(round, roundIndex) in takeRound"
  )
    .round(
      :key="roundIndex"
      :class="['round-' + (roundIndex+1), takeRoundModifier]"
    )
      .match(
        v-for="(match, matchIndex) in round"
        :key="matchIndex"
        :class="getMatchClass(match, roundIndex, matchIndex)"
      )
        template(v-if="match !== null")
          .robot(
            v-for="(team, teamIndex) in match"
            :key="teamIndex"
            :class="getRobotClass(match, roundIndex, matchIndex, teamIndex)"
          )
            .content
              span(:class="!team ? 'tbd' : ''") {{team || '-'}}

</template>

<script>
export default {
  name: 'TournamentBrackets',
  props: {
    side: {
      type: String,
      required: false,
      validator: (value) =>
        ['Left', 'Right'].includes(value),
      default: () => 'Right'
    },
    bracketSize: {
      type: Number,
      default: 4
    },
    items: {
      type: Array,
      default: []
    },
    // parallel to items: wins[roundIndex][matchIndex] = 0 (red won) | 1 (blue won) | null (undecided)
    wins: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    takeModifier () {
      return this.side === 'Left' ? ['brackets--left'] : ['brackets--right']
    },
    takeRoundModifier () {
      return this.side === 'Left' ? 'round--left' : 'round--right'
    },
    takeRound () {
      return this.items || []
    }
  },
  methods: {
    winnerSide (roundIndex, matchIndex) {
      const round = this.wins && this.wins[roundIndex]
      if (!round) return null
      const w = round[matchIndex]
      return (w === 0 || w === 1) ? w : null
    },
    getMatchClass (match, roundIndex, matchIndex) {
      const sideClass = this.side === 'Left' ? 'match--left' : 'match--right'
      if (match === null) return ['match--gap', sideClass]
      if (match.length === 1) return ['match--last', sideClass]
      return [`match-${roundIndex + 1}-${matchIndex + 1}`, sideClass]
    },
    getRobotClass (match, roundIndex, matchIndex, teamIndex) {
      const sideClass = this.side === 'Left' ? 'robot--left' : 'robot--right'
      const classes = []
      if (match.length === 1) {
        classes.push('robot--last', sideClass)
      } else {
        classes.push(`robot-${roundIndex + 1}-${matchIndex + 1}-${teamIndex + 1}`, sideClass)
      }
      // canto vermelho (index 0) x azul (index 1) — igual à identidade red vs blue do app
      classes.push(teamIndex === 0 ? 'robot--red' : 'robot--blue')
      const w = this.winnerSide(roundIndex, matchIndex)
      if (w !== null) {
        classes.push(teamIndex === w ? 'robot--winner' : 'robot--loser')
      }
      return classes
    }
  }
}
</script>

<style lang="sass" scoped>
  $blue: #287AED
  $red: #DD3434
  $green: #2E9E4F
  $ink: #0B0E14
  $muted: #5C6673
  $line: #D7DCE5
  $wire: #C3CBD8
  $paper: #FFFFFF
  $display: 'Saira Condensed', 'Lato', sans-serif
  $mono: 'Space Mono', 'Lato', monospace

  .brackets
    display: flex
    width: 100%
    &--right
      flex-direction: row-reverse

    .round
      display: flex
      flex-grow: 1
      flex-direction: column
      max-width: 210px
      .match
        display: flex
        flex-direction: column
        justify-content: center
        margin: 0 10px
        padding: 6px 6px
        flex-grow: 1
        position: relative
        &--gap
          visibility: hidden
          pointer-events: none
          &::before
            display: none

        &--last
          &.match--left, &.match--right
            &::before
              display: none

        &::before
          content: ""
          display: block
          width: 48px
          border-bottom: 2px solid $wire
          position: absolute
          top: 50%

        &--right
          &::before
            margin-right: 0px
            left: -38px

        &--left
          &::before
            margin-left: 0px
            right: -42px

        .robot
          display: flex
          flex-direction: column
          justify-content: center
          margin: 8px 16px
          flex-grow: 1
          position: relative
          &:nth-child(odd)
            &::after
              content: ""
              display: block
              border: 2px solid transparent
              height: 100%
              position: absolute
              width: 10px
              top: 50%

          &:nth-child(even)
            &::after
              content: ""
              display: block
              border: 2px solid transparent
              height: 100%
              position: absolute
              width: 10px
              bottom: 50%

          &--left
            &:nth-child(odd)
              &::after
                border-top-color: $wire
                border-right-color: $wire
                right: -16px

            &:nth-child(even)
              &::after
                border-bottom-color: $wire
                border-right-color: $wire
                right: -16px

          &--right
            &:nth-child(odd)
              &::after
                border-top-color: $wire
                border-left-color: $wire
                left: -13px

            &:nth-child(even)
              &::after
                border-bottom-color: $wire
                border-left-color: $wire
                left: -13px

          &--last
            &:nth-child(odd)
              &::after
                display: none

            &:nth-child(even)
              &::after
                display: none

          // --- canto vermelho x azul (borda esquerda da placa) ---
          &--red
            .content::before
              background: $red
          &--blue
            .content::before
              background: $blue

          // --- vencedor / perdedor ---
          &--winner
            .content
              border-color: rgba(46, 158, 79, 0.55)
              background: #EAF7EF
              font-weight: 700
              color: #14532D
              box-shadow: 0 2px 8px rgba(46, 158, 79, 0.18)
              padding-right: 22px
              &::after
                content: "✓"
                position: absolute
                right: 7px
                top: 50%
                transform: translateY(-50%)
                color: $green
                font-size: 12px
                font-weight: 700

          &--loser
            .content
              color: $muted
              background: #F4F6FA
              span:not(.tbd)
                text-decoration: line-through
                text-decoration-color: rgba(92, 102, 115, 0.5)

          .content
            border: 1px solid $line
            border-radius: 7px
            width: 100%
            height: 34px
            position: relative
            display: flex
            align-items: center
            justify-content: center
            overflow: hidden
            padding: 0 10px 0 14px
            background: $paper
            font-family: $display
            font-size: 15px
            font-weight: 600
            letter-spacing: 0.01em
            color: $ink
            box-shadow: 0 1px 2px rgba(11, 14, 20, 0.05)
            transition: box-shadow .16s ease, transform .16s ease, border-color .16s ease
            // faixa de canto (vermelho/azul), pintada por robot--red/--blue
            &::before
              content: ""
              position: absolute
              left: 0
              top: 0
              bottom: 0
              width: 4px
              background: $wire
            &:hover
              border-color: rgba(40, 122, 237, 0.5)
              box-shadow: 0 3px 10px rgba(11, 14, 20, 0.12)
              transform: translateY(-1px)
            span
              overflow: hidden
              text-overflow: ellipsis
              white-space: nowrap
            .tbd
              color: #AEB7C4
              font-family: $mono
              font-size: 11px
              font-weight: 400
              text-transform: uppercase
              letter-spacing: 0.08em
</style>
